// ================== Component file ==================
// TitleBlockTab.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Box,
  VStack,
  Container,
  Heading,
  FormLabel,
  useToast,
  useColorModeValue,
  Spinner,
  Text,
  Flex,
} from '@chakra-ui/react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import {
  submitTitleBlock,
} from '../../InventoryServices/TitleBlockTabAPI/TitleBlockTabAPI';
// FIX: 'fetchTitleBlock' (from TitleBlockTabAPI) galat/alag endpoint
// (`/rsform/inventories/titleBlock/${id}`) hit karta tha jiska data
// prefill ke liye reliable nahi tha. Iski jagah 'getFormStructureDetails'
// use kar rahe hain — ye wahi "details" API hai (`/rsform/inventories/${id}/details`)
// jo InfoTab.jsx me already successfully use ho rahi hai aur jisme
// `data.title_block` field verified real data ke saath aata hai.
import { getFormStructureDetails } from '../../InventoryServices/InventoryInfoTabs/InfoTabsAPI';

const TitleBlockTab = ({ title_block, onTitleBlockUpdated }) => {
  const { id } = useParams();
  const toast = useToast();

  const [editorValue, setEditorValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Prevent overwrite on first load
  const isPrefilledFromProps = useRef(false);

  const headerColor = useColorModeValue('blue.500', 'blue.300');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // ===============================
  // Prefill from props (initial)
  // ===============================
  useEffect(() => {
    if (title_block && !isPrefilledFromProps.current) {
      setEditorValue(title_block);
      isPrefilledFromProps.current = true;
    }
  }, [title_block]);

  // ===============================
  // Fetch fallback (only once)
  // ===============================
  useEffect(() => {
    const loadTitleBlock = async () => {
      if (!id || isPrefilledFromProps.current) return;

      setIsLoading(true);
      try {
        // FIX: pehle 'fetchTitleBlock(id)' call hoti thi jo galat endpoint
        // (`/rsform/inventories/titleBlock/${id}`) hit karti thi aur uska
        // response bhi galat shape se parse ho raha tha
        // (response?.data?.title_block — ek '.data' level miss tha, sahi
        // hota response?.data?.data?.title_block).
        // Ab 'getFormStructureDetails' use ho rahi hai — ye pehle se hi
        // 'res.data' return karti hai, isliye sirf ek '.data' level chahiye.
        const response = await getFormStructureDetails(id);
        if (response?.status && response?.data?.title_block) {
          setEditorValue(response.data.title_block);
        }
      } catch (error) {
        console.error('Fetch title block error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTitleBlock();
  }, [id]);

  const handleChange = (value) => {
    setEditorValue(value);
  };

  // ===============================
  // Submit + Refresh Inventory
  // ===============================
  const handleSubmitTitleBlock = async () => {
    if (!id) {
      toast({
        title: 'Error',
        description: 'Inventory ID not found',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const strippedValue = editorValue.replace(/<[^>]*>/g, '').trim();
    if (!strippedValue) {
      toast({
        title: 'Validation Error',
        description: 'Title block content cannot be empty',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // 🔹 Update API
      const response = await submitTitleBlock(id, editorValue);

      if (response?.status === 200 || response?.data?.status) {
        toast({
          title: 'Success',
          description: 'Title block updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // ===============================
        // 🔥 REFRESH INVENTORY DATA
        // ===============================
        // FIX: pehle 'GetInventoryData(id)' call hoti thi — ye actually
        // LIST endpoint (`/rsform/inventories`) hai jo {limit, page, filters}
        // expect karta hai, id string nahi. Isse single record ka
        // title_block kabhi sahi se nahi milta tha. Ab wahi
        // 'getFormStructureDetails(id)' use ho raha hai jo prefill me bhi
        // use ho rahi hai — consistent aur correct.
        const inventoryRes = await getFormStructureDetails(id);

        const freshTitleBlock = inventoryRes?.data?.title_block;

        if (freshTitleBlock) {
          // reset lock
          isPrefilledFromProps.current = false;

          // update editor with fresh content
          setEditorValue(freshTitleBlock);

          // lock again
          isPrefilledFromProps.current = true;
        }
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      toast({
        title: 'Submission Failed',
        description:
          error.response?.data?.message ||
          error.message ||
          'Something went wrong',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ===============================
  // Render
  // ===============================
  return (
    <Container maxW="container.md" p={6}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" color={headerColor}>
            Title Block Editor
          </Heading>
          <Text fontSize="sm" mt={1} color="gray.500">
            Inventory ID:{' '}
            <Text as="span" fontWeight="bold">
              {id || 'Not Found'}
            </Text>
          </Text>
        </Box>

        <Box
          p={4}
          borderWidth={1}
          borderRadius="md"
          borderColor={borderColor}
          boxShadow="sm"
        >
          <FormLabel fontWeight="semibold">
            Enter Title Block Content
          </FormLabel>

          {isLoading ? (
            <Flex justify="center" align="center" h="300px">
              <Spinner size="lg" mr={3} />
              <Text>Loading title block...</Text>
            </Flex>
          ) : (
            <Box pb="42px">
              <ReactQuill
                value={editorValue}
                onChange={handleChange}
                theme="snow"
                style={{ height: '280px' }}
              />
            </Box>
          )}
        </Box>

        <Button
          colorScheme="blue"
          size="lg"
          isLoading={isSubmitting}
          isDisabled={isSubmitting || isLoading || !id}
          onClick={handleSubmitTitleBlock}
        >
          Submit Title Block
        </Button>

        {process.env.NODE_ENV === 'development' && (
          <Box p={3} bg="gray.100" borderRadius="md" fontSize="xs">
            <Text fontWeight="bold">Debug Info</Text>
            <Text>ID: {id}</Text>
            <Text>Content length: {editorValue.length}</Text>
          </Box>
        )}
      </VStack>
    </Container>
  );
};

export default TitleBlockTab;